// services/orderBoxInvoice.service.js
//
// Member-facing OrderBox invoice endpoints (Phase 2 Step 2):
//   GET  /order-boxes/{id}/invoice            — fetch the freshest invoice payload
//   POST /order-boxes/{id}/confirm-delivery   — member confirms delivery (settles wallet hold)
//
// Both endpoints flow through jsonApiClient so they reuse the Sanctum SPA cookie +
// X-XSRF-TOKEN + the shared 401/419 interceptor. Do NOT introduce a new axios
// instance — see the comment block in src/httpConfig/config/axios.js.
//
// Error normalization mirrors src/services/orderBoxItem.service.js. The 409
// `invoice_version_mismatch` body is a *flat* object (not JSON:API), so it is
// inspected BEFORE the JSON:API detail extractor and surfaced as a typed
// InvoiceVersionMismatchError carrying the server's current invoice version so
// callers can re-GET and surface the diff to the user.
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import { apiClient, jsonApiClient } from '@/httpConfig/config/axios.js';

export const INVOICE_VERSION_MISMATCH = 'invoice_version_mismatch';
export const INVOICE_INVALID_STATE = 'invoice_invalid_state';
export const INVOICE_FORBIDDEN = 'invoice_forbidden';
export const INVOICE_EMPTY_BODY = 'invoice_empty_body';

/**
 * Backend serializes OrderBox.status as the semantic string ("SHIPPED"), but
 * the model + OrderBoxStatusMeta key off the numeric enum (5). Coerce once at
 * the service boundary so call sites can rely on `status === OrderBoxStatus.SHIPPED`.
 */
const STATUS_STRING_TO_ENUM = {
  DRAFT:     OrderBoxStatus.DRAFT,
  ACTIVE:    OrderBoxStatus.ACTIVE,
  READY:     OrderBoxStatus.READY,
  PACKED:    OrderBoxStatus.PACKED,
  SHIPPED:   OrderBoxStatus.SHIPPED,
  DELIVERED: OrderBoxStatus.DELIVERED,
  CANCELLED: OrderBoxStatus.CANCELLED,
  CLOSED:    OrderBoxStatus.CLOSED,
};

export function coerceStatus(value) {
  if (typeof value === 'string' && STATUS_STRING_TO_ENUM[value] !== undefined) {
    return STATUS_STRING_TO_ENUM[value];
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return value;
}

/**
 * Thrown when POST /confirm-delivery returns 409 because the admin edited the
 * invoice between the member's read and write. Carries the new `currentVersion`
 * so the caller can refetch + show a diff before re-confirming.
 */
export class InvoiceVersionMismatchError extends Error {
  constructor(currentVersion) {
    super('invoice_version_mismatch');
    this.name = 'InvoiceVersionMismatchError';
    this.code = INVOICE_VERSION_MISMATCH;
    this.status = 409;
    this.currentVersion = currentVersion;
  }
}

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

function normalizeError(error) {
  // Programmer-error throws (e.g. INVOICE_EMPTY_BODY from extractInvoice) carry
  // their own code and have no response — don't re-wrap them through the HTTP
  // error path or we'd erase the tag.
  if (error?.code === INVOICE_EMPTY_BODY) return error;

  const status = error?.response?.status;
  const body = error?.response?.data;

  // 409 has a FLAT body — not JSON:API — so inspect it BEFORE the detail extractor.
  if (status === 409 && body?.error === INVOICE_VERSION_MISMATCH) {
    return new InvoiceVersionMismatchError(body.current_invoice_version);
  }

  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) normalized.code = INVOICE_FORBIDDEN;
  else if (status === 422) normalized.code = INVOICE_INVALID_STATE;

  return normalized;
}

function extractInvoice(response) {
  const data = response?.data?.data;
  if (!data) {
    // A 2xx response with an empty body is a server bug. For confirmDelivery
    // this is dangerous — the wallet hold may have already settled — so we
    // tag the error so the caller can render a "may have succeeded, refresh
    // to verify" message instead of a generic retry alert that risks the user
    // re-POSTing against a now-CLOSED box.
    const err = new Error('Empty response from invoice endpoint');
    err.code = INVOICE_EMPTY_BODY;
    throw err;
  }
  if (data.status !== undefined && data.status !== null) {
    data.status = coerceStatus(data.status);
  }
  return data;
}

/**
 * Fetch the invoice payload for an OrderBox. Owner-only; 403 → not yours,
 * 404 → unknown id. Use `?include=items` to hydrate line items in one round trip.
 *
 * @param {string|number} boxId
 * @returns {Promise<object>} flat (deserialized) JSON:API resource with invoice fields
 */
export async function fetchInvoice(boxId) {
  try {
    const response = await jsonApiClient.get(`/order-boxes/${boxId}/invoice`, {
      params: { include: 'items' },
    });
    return extractInvoice(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Member confirms delivery, settling the wallet hold. Irreversible.
 * Must echo the `invoiceVersion` returned by the matching `fetchInvoice` call.
 *
 * On 409 throws InvoiceVersionMismatchError — DO NOT silently retry; the caller
 * must refetch, surface what changed, then re-confirm with the new version.
 *
 * @param {string|number} boxId
 * @param {number} invoiceVersion
 * @returns {Promise<object>} the updated OrderBox resource (status now CLOSED)
 */
export async function confirmDelivery(boxId, invoiceVersion) {
  try {
    const response = await jsonApiClient.post(
      `/order-boxes/${boxId}/confirm-delivery`,
      { invoice_version: invoiceVersion },
    );
    return extractInvoice(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Member marks the box as delivered ("I received my order") — a physical-receipt
 * acknowledgement that transitions SHIPPED → DELIVERED. Moves NO money (settlement
 * is a separate, admin-only step). No body: the delivered instant is always server
 * `now()`. No `invoice_version` (nothing to settle, so no optimistic lock).
 *
 * Errors (via normalizeError): 403 → INVOICE_FORBIDDEN (not the owner), 422 →
 * INVOICE_INVALID_STATE (box not SHIPPED — includes a second call on an already-
 * DELIVERED box). There is no 409 path here.
 *
 * @param {string|number} boxId
 * @returns {Promise<object>} the updated OrderBox resource (status now DELIVERED)
 */
export async function markDelivered(boxId) {
  try {
    const response = await jsonApiClient.post(`/order-boxes/${boxId}/mark-delivered`);
    return extractInvoice(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * A short-lived download URL for the carton's invoice PDF (the carton account).
 * The backend regenerates a stale PDF on this read, and answers a FLAT
 * `{ download_url }` body — not JSON:API — so this goes through `apiClient`:
 * jsona would gut a body with no `data` member.
 *
 * Errors (via normalizeError): 403 → INVOICE_FORBIDDEN; 404 → no invoice yet.
 *
 * @param {string|number} boxId
 * @returns {Promise<string>} the temporary URL (valid ~10 minutes)
 */
export async function fetchInvoicePdfUrl(boxId) {
  try {
    const response = await apiClient.get(`/order-boxes/${boxId}/invoice.pdf`);
    const url = response?.data?.download_url;
    if (!url) throw new Error('invoice_pdf_missing_url');
    return url;
  } catch (error) {
    throw normalizeError(error);
  }
}
